import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { useIsTerminalScreen } from '../../../../hooks/useIsTerminalScreen';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import TerminalScreenMessage from '../../../../components/navigation/TerminalScreenMessage';
import { Heading } from '../../../../components/Heading';
import { classNames } from '../../../../style/classNames';
import type { Chart, PatientDoc } from '@usacsmideo/types';
import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/$patientId/card/addWidget')({
  component: AddWidgetPage,
});

function AddWidgetPage() {
  const { currentUser } = useAuth();
  const patientId = currentUser?.uid || '';
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const isTerminal = useIsTerminalScreen(pathname, patientInfo?.navObj);

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo,
      chart,
    });

    if ('incompleteMessage' in res) {
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    if ('complete' in res) {
      mutate(
        { patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/$patientId', params: { patientId } });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }

    mutate(
      { patientId, navObj: res.updatedNavObj },
      {
        onSuccess: () => {
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  if (!patientInfo || !patientInfo.navObj) {
    return <p>Loading...</p>;
  }

  return (
    <div className="flex w-full flex-col items-center">
      <Heading id="add-widget" textContent="Add Lock Screen Widget" />
      <div className="mt-4 max-w-lg text-center">
        <p className="text-xl">
          The MIDEO lock screen widget puts your emergency medical information right on your phone&apos;s lock screen,
          so it&apos;s always accessible even without unlocking your device.
        </p>
        <p className="mt-4 text-xl">
          This feature is available exclusively through the MIDEO Health mobile app. Download the app to get started.
        </p>
      </div>
      <div className="mt-8 flex flex-col items-center gap-6 sm:flex-row">
        <a
          href="https://apps.apple.com/app/mideo-card/id1538827265"
          target="_blank"
          rel="noopener noreferrer"
          className="transition-opacity hover:opacity-80"
        >
          <img
            src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1716768000"
            alt="Download on the App Store"
            className="h-14"
          />
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=com.mideocard.mideohealth"
          target="_blank"
          rel="noopener noreferrer"
          className="transition-opacity hover:opacity-80"
        >
          <img
            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
            alt="Get it on Google Play"
            className="h-20"
          />
        </a>
      </div>
      {isTerminal ? (
        <TerminalScreenMessage onReturnToOverview={handleNextButtonClick} />
      ) : (
        <button onClick={handleNextButtonClick} className={classNames.nextButton}>
          Next
        </button>
      )}
    </div>
  );
}
